/**
 * PMGT FE-2 — the shared stepper SHELL for the unified project wizard.
 *
 * Renders the non-linear MUI Stepper (FB-10), the required-fields legend, the
 * holistic error banner (FB-08), and the Back / Cancel / primary-action footer.
 * The four step BODIES are passed in by the mode-specific orchestrators
 * (create vs. edit) via `renderStep(activeStep)`. The shell owns NO domain
 * state — it is pure layout + navigation chrome, so create and edit share the
 * exact same flow indication.
 */
import { type ReactElement, type ReactNode } from "react";
import {
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
  Typography,
} from "@mui/material";

export interface WizardShellProps {
  stepLabels: readonly string[];
  activeStep: number;
  onStepChange: (index: number) => void;
  errorSteps: ReadonlySet<number>;
  /** Body of the currently active step. */
  children: ReactNode;
  /** Shown above the footer when the holistic validation has errors. */
  showErrorBanner: boolean;
  /** A free-form submit/save error (e.g. a rejected request). */
  submitError: string | null;
  /** The label of the terminal primary action ("Create project" / "Save"). */
  primaryLabel: string;
  /** Whether the primary action is shown on the active step. Create shows it
   *  only on the last step; edit may show a per-step Save. */
  showPrimaryAction: boolean;
  primaryDisabled: boolean;
  onPrimary: () => void;
  onBack: () => void;
  onNext: () => void;
  onCancel: () => void;
  /** Hide Next on the terminal step (create) — edit keeps Next as plain nav. */
  showNext: boolean;
  /** Optional extra node rendered between the error banner and the footer. */
  footerExtra?: ReactNode;
}

export function WizardShell({
  stepLabels,
  activeStep,
  onStepChange,
  errorSteps,
  children,
  showErrorBanner,
  submitError,
  primaryLabel,
  showPrimaryAction,
  primaryDisabled,
  onPrimary,
  onBack,
  onNext,
  onCancel,
  showNext,
  footerExtra,
}: WizardShellProps): ReactElement {
  return (
    <>
      <Stepper nonLinear activeStep={activeStep} sx={{ mt: 3, mb: 1 }}>
        {stepLabels.map((label, index) => (
          <Step key={label}>
            <StepButton
              color="inherit"
              onClick={() => {
                onStepChange(index);
              }}
            >
              <StepLabel error={errorSteps.has(index)}>{label}</StepLabel>
            </StepButton>
          </Step>
        ))}
      </Stepper>

      <Typography variant="caption" color="text.secondary">
        Fields marked{" "}
        <Box component="span" sx={{ color: "error.main" }}>
          *
        </Box>{" "}
        are required.
      </Typography>

      <Box sx={{ mt: 3 }} component="form">
        {children}

        {showErrorBanner ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 3 }}>
            Some required fields need attention. The steps with errors are
            flagged above — fix the highlighted fields, then continue.
          </Alert>
        ) : null}

        {submitError !== null ? (
          <Alert severity="error" variant="outlined" sx={{ mt: 2 }}>
            {submitError}
          </Alert>
        ) : null}

        {footerExtra}

        <Divider sx={{ my: 3 }} />

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Button variant="text" disabled={activeStep === 0} onClick={onBack}>
            Back
          </Button>

          <Stack direction="row" spacing={2}>
            <Button variant="text" onClick={onCancel}>
              Cancel
            </Button>
            {showPrimaryAction ? (
              <Button
                variant="contained"
                disabled={primaryDisabled}
                onClick={onPrimary}
              >
                {primaryLabel}
              </Button>
            ) : null}
            {showNext ? (
              <Button variant="contained" onClick={onNext}>
                Next
              </Button>
            ) : null}
          </Stack>
        </Stack>
      </Box>
    </>
  );
}
